import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "../../models/EndpointsEnum";
import IOrder from "../../models/IOrder";
import IGetOrdersDocumentsDTO from "../../models/DTO/IGetOrdersDocumentsDTO";
import IOnGetOrdersDocumentsDTO from "../../models/DTO/IOnGetOrdersDocumentsDTO";

/**
 * Reads the order guids a bulk document view (Sales Order / Invoice) should render from the query
 * string. Accepts the multi-order `?orders=a,b,c` form the Orders screen sends, and the legacy
 * single-order `?order=a` form so older links keep working. Blank entries and duplicates are dropped
 * and the original order is kept.
 */
export const parseBulkDocumentOrderGuids = (search: string): string[] => {
    const params = new URLSearchParams(search);

    const raw = [params.get("orders") || "", params.get("order") || ""].join(",");

    const seen = new Set<string>();
    const orderGuids: string[] = [];

    for (const entry of raw.split(",")) {
        const guid = entry.trim();

        if (guid && !seen.has(guid)) {
            seen.add(guid);
            orderGuids.push(guid);
        }
    }

    return orderGuids;
};

/**
 * Loads every order named in the query string through the bulk documents socket endpoint and
 * returns them in the requested order, for views that print one document per order.
 *
 * @param documentTitle Shown in the browser tab, e.g. "Sales Order" or "Invoice".
 */
export const useBulkDocumentOrders = (documentTitle: string) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const location = useLocation();

    const orderGuids = useMemo(() => parseBulkDocumentOrderGuids(location.search), [location.search]);

    const [orders, setOrders] = useState<IOrder[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);

    useEffect(() => {
        const onGetOrdersDocuments = (data: IOnGetOrdersDocumentsDTO) => {
            const _orders = data.orders || [];

            const orderNames = _orders.map((order) => order.name);

            document.title = `Orderwerks >> ${documentTitle} >> ${orderNames.join(", ")}`;

            setOrders(_orders);
            setLoaded(true);
        };

        socket.on(EndpointsEnum.OrderOnGetOrdersDocuments, onGetOrdersDocuments);

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersDocuments, onGetOrdersDocuments);
        };
    }, [socket, documentTitle]);

    useEffect(() => {
        if (!orderGuids.length) {
            setOrders([]);
            setLoaded(true);

            return;
        }

        // Drop the previous selection's documents so a still-mounted page can't show or print them
        // while the new batch is loading.
        setLoaded(false);
        setOrders([]);

        const payload: IGetOrdersDocumentsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuids,
        };

        socket.emit(EndpointsEnum.OrderGetOrdersDocuments, payload);
    }, [orderGuids, socket, userModel.guid, userModel.token, supplierModel.guid]);

    return { orderGuids, orders, loaded };
};
